<template>
  <div class="tw-space-y-4 tw-p-2">

    <!-- Welcome Header -->
    <div class="tw-bg-gradient-to-r tw-from-primary-500 tw-to-primary-600 tw-rounded-lg tw-p-6 tw-text-white">
      <div v-if="!overviewLoading" class="tw-flex tw-items-center tw-justify-between">
        <div>
          <h1 class="tw-text-2xl tw-font-bold tw-mb-2">{{ selectedWelcome.message }}</h1>
          <p class="tw-text-primary-100 tw-text-sm">{{ selectedWelcome.subtitle }}</p>
        </div>
        <div class="tw-text-right">
          <div class="tw-text-3xl tw-font-bold tw-capitalize">{{ getCurrentDate() }}</div>
          <div class="tw-text-primary-100 tw-text-sm">{{ getCurrentTime() }}</div>
        </div>
      </div>
      <div v-else class="tw-animate-pulse">
        <div class="tw-h-8 tw-bg-white tw-bg-opacity-20 tw-rounded tw-w-64 tw-mb-2"></div>
        <div class="tw-h-4 tw-bg-white tw-bg-opacity-20 tw-rounded tw-w-80"></div>
      </div>
    </div>

    <!-- ── Platform Overview ──────────────────────────────── -->
    <div class="tw-flex tw-items-center tw-gap-2">
      <iconify-icon icon="solar:widget-bold-duotone" class="tw-text-primary-500 tw-text-base" />
      <h2 class="tw-text-sm tw-font-semibold tw-text-gray-700">Platform Overview</h2>
      <div class="tw-flex-1 tw-h-px tw-bg-gray-200 tw-ml-1" />
    </div>
    <div class="tw-grid tw-grid-cols-2 md:tw-grid-cols-4 tw-gap-2">
      <DashboardStatsCard
        :loading="overviewLoading"
        :title="$t('dashboard.admin.stats.total-companies')"
        :value="overviewStats.companies ?? 0"
        icon="solar:buildings-2-bold-duotone"
        icon-color="tw-text-purple-600"
        icon-bg-color="tw-bg-purple-100"
      />
      <DashboardStatsCard
        :loading="overviewLoading"
        :title="$t('dashboard.admin.stats.total-users')"
        :value="overviewStats.users ?? 0"
        icon="solar:users-group-rounded-bold-duotone"
        icon-color="tw-text-blue-600"
        icon-bg-color="tw-bg-blue-100"
      />
      <DashboardStatsCard
        :loading="overviewLoading"
        :title="$t('dashboard.admin.stats.total-products')"
        :value="overviewStats.products ?? 0"
        icon="solar:box-bold-duotone"
        icon-color="tw-text-green-600"
        icon-bg-color="tw-bg-green-100"
      />
      <DashboardStatsCard
        :loading="overviewLoading"
        :title="$t('dashboard.admin.stats.total-services')"
        :value="overviewStats.services ?? 0"
        icon="solar:settings-bold-duotone"
        icon-color="tw-text-indigo-600"
        icon-bg-color="tw-bg-indigo-100"
      />
    </div>

    <!-- ── Meetings ───────────────────────────────────────── -->
    <div class="tw-flex tw-items-center tw-gap-2">
      <iconify-icon icon="solar:calendar-bold-duotone" class="tw-text-violet-500 tw-text-base" />
      <h2 class="tw-text-sm tw-font-semibold tw-text-gray-700">Meetings</h2>
      <div class="tw-flex-1 tw-h-px tw-bg-gray-200 tw-ml-1" />
    </div>
    <div class="tw-grid tw-grid-cols-2 md:tw-grid-cols-3 lg:tw-grid-cols-6 tw-gap-2">
      <DashboardStatsCard
        :loading="statsLoading"
        title="Meetings total"
        :value="meetingStats.total ?? 0"
        icon="solar:calendar-bold-duotone"
        icon-color="tw-text-violet-600"
        icon-bg-color="tw-bg-violet-100"
      />
      <DashboardStatsCard
        :loading="statsLoading"
        title="Completed"
        :value="meetingStats.completed ?? 0"
        icon="solar:calendar-mark-bold-duotone"
        icon-color="tw-text-emerald-600"
        icon-bg-color="tw-bg-emerald-100"
      />
      <DashboardStatsCard
        :loading="statsLoading"
        title="Requests sent"
        :value="meetingStats.requests_sent ?? 0"
        icon="solar:inbox-line-duotone"
        icon-color="tw-text-sky-600"
        icon-bg-color="tw-bg-sky-100"
      />
      <DashboardStatsCard
        :loading="statsLoading"
        title="Requests accepted"
        :value="meetingStats.requests_accepted ?? 0"
        icon="solar:check-circle-bold-duotone"
        icon-color="tw-text-lime-600"
        icon-bg-color="tw-bg-lime-100"
      />
      <DashboardStatsCard
        :loading="statsLoading"
        title="Conversations"
        :value="meetingStats.conversations ?? 0"
        icon="solar:chat-round-dots-bold-duotone"
        icon-color="tw-text-fuchsia-600"
        icon-bg-color="tw-bg-fuchsia-100"
      />
      <DashboardStatsCard
        :loading="statsLoading"
        :title="`Conversion rate`"
        :value="meetingStats.conversion_rate != null ? `${meetingStats.conversion_rate}%` : '0%'"
        icon="solar:graph-up-bold-duotone"
        icon-color="tw-text-amber-600"
        icon-bg-color="tw-bg-amber-100"
      />
    </div>

    <!-- ── Views & Engagement — header is rendered inside the widget ── -->
    <ViewsAnalyticsWidget />

    <!-- ── Activity & Categories ──────────────────────────── -->
    <div class="tw-flex tw-items-center tw-gap-2">
      <iconify-icon icon="solar:chart-bold-duotone" class="tw-text-emerald-500 tw-text-base" />
      <h2 class="tw-text-sm tw-font-semibold tw-text-gray-700">Activity &amp; Categories</h2>
      <div class="tw-flex-1 tw-h-px tw-bg-gray-200 tw-ml-1" />
    </div>
    <div class="tw-grid tw-grid-cols-1 lg:tw-grid-cols-3 tw-gap-2">

      <!-- Meetings per company / participant -->
      <div class="tw-bg-white tw-rounded-lg tw-p-4 tw-border tw-border-solid tw-border-gray-200">
        <div class="tw-flex tw-items-center tw-justify-between tw-mb-2">
          <h3 class="tw-text-sm tw-font-semibold tw-text-gray-900">Meetings per company / participant</h3>
          <span class="tw-text-xs tw-text-gray-400">Top pairs</span>
        </div>

        <!-- Loading -->
        <div v-if="statsLoading" class="tw-space-y-2">
          <div v-for="i in 5" :key="i" class="tw-h-6 tw-bg-gray-100 tw-rounded tw-animate-pulse" />
        </div>

        <!-- Empty -->
        <div v-else-if="!companyPairs.length" class="tw-py-6 tw-text-center tw-text-xs tw-text-gray-400">
          No meeting data yet
        </div>

        <!-- Data -->
        <table v-else class="tw-w-full tw-text-xs tw-text-left tw-text-gray-600">
          <thead>
            <tr class="tw-border-b tw-border-gray-100">
              <th class="tw-py-1.5 tw-pr-2 tw-font-medium tw-text-gray-400">From</th>
              <th class="tw-py-1.5 tw-pr-2 tw-font-medium tw-text-gray-400">To</th>
              <th class="tw-py-1.5 tw-text-right tw-font-medium tw-text-gray-400">Meetings</th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="pair in companyPairs"
              :key="`${pair.from}-${pair.to}`"
              class="tw-border-b tw-border-gray-50 last:tw-border-b-0"
            >
              <td class="tw-py-1.5 tw-pr-2 tw-text-gray-800 tw-truncate tw-max-w-[100px]">{{ pair.from }}</td>
              <td class="tw-py-1.5 tw-pr-2 tw-text-gray-800 tw-truncate tw-max-w-[100px]">{{ pair.to }}</td>
              <td class="tw-py-1.5 tw-text-right tw-font-semibold tw-text-gray-900">{{ pair.meetings }}</td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- Best performing categories -->
      <div class="tw-bg-white tw-rounded-lg tw-p-4 tw-border tw-border-solid tw-border-gray-200">
        <div class="tw-flex tw-items-center tw-justify-between tw-mb-3">
          <h3 class="tw-text-sm tw-font-semibold tw-text-gray-900">Best performing categories</h3>
          <span class="tw-text-xs tw-text-gray-400">By companies</span>
        </div>

        <!-- Loading -->
        <div v-if="statsLoading" class="tw-space-y-2">
          <div v-for="i in 4" :key="i" class="tw-h-8 tw-bg-gray-100 tw-rounded tw-animate-pulse" />
        </div>

        <!-- Empty -->
        <div v-else-if="!bestCategories.length" class="tw-py-6 tw-text-center tw-text-xs tw-text-gray-400">
          No categories yet
        </div>

        <!-- Data -->
        <div v-else class="tw-space-y-2">
          <div
            v-for="(cat, index) in bestCategories"
            :key="cat.name"
            class="tw-space-y-1"
          >
            <div class="tw-flex tw-items-center tw-justify-between">
              <div class="tw-flex tw-items-center tw-gap-1.5">
                <span class="tw-text-[10px] tw-font-semibold tw-text-gray-400">#{{ index + 1 }}</span>
                <span class="tw-text-xs tw-font-medium tw-text-gray-800">{{ cat.name }}</span>
              </div>
              <span class="tw-text-xs tw-text-gray-500">{{ cat.companies }} companies</span>
            </div>
            <div class="tw-w-full tw-bg-gray-100 tw-rounded-full tw-h-1.5">
              <div
                class="tw-h-1.5 tw-rounded-full tw-bg-indigo-500"
                :style="{ width: maxCategoryCompanies > 0 ? `${(cat.companies / maxCategoryCompanies) * 100}%` : '0%' }"
              />
            </div>
          </div>
        </div>
      </div>

      <!-- User activity -->
      <div class="tw-bg-white tw-rounded-lg tw-p-4 tw-border tw-border-solid tw-border-gray-200">
        <div class="tw-flex tw-items-center tw-justify-between tw-mb-3">
          <h3 class="tw-text-sm tw-font-semibold tw-text-gray-900">User activity</h3>
          <span class="tw-text-xs tw-text-gray-400">Lifecycle</span>
        </div>

        <!-- Loading -->
        <div v-if="statsLoading" class="tw-grid tw-grid-cols-2 tw-gap-3">
          <div v-for="i in 5" :key="i" class="tw-h-10 tw-bg-gray-100 tw-rounded tw-animate-pulse" />
        </div>

        <!-- Data / Empty -->
        <div v-else class="tw-grid tw-grid-cols-2 tw-gap-3">
          <div class="tw-space-y-0.5">
            <p class="tw-text-[11px] tw-text-gray-500">Finished onboarding</p>
            <p class="tw-text-base tw-font-semibold tw-text-emerald-600">{{ userActivity.finished_onboarding ?? 0 }}</p>
          </div>
          <div class="tw-space-y-0.5">
            <p class="tw-text-[11px] tw-text-gray-500">Online now</p>
            <p class="tw-text-base tw-font-semibold tw-text-sky-600">{{ userActivity.online_now ?? 0 }}</p>
          </div>
          <div class="tw-space-y-0.5">
            <p class="tw-text-[11px] tw-text-gray-500">Inactive users</p>
            <p class="tw-text-base tw-font-semibold tw-text-rose-600">{{ userActivity.inactive_users ?? 0 }}</p>
          </div>
          <div class="tw-space-y-0.5">
            <p class="tw-text-[11px] tw-text-gray-500">Pending onboarding</p>
            <p class="tw-text-base tw-font-semibold tw-text-amber-600">{{ userActivity.pending_onboarding ?? 0 }}</p>
          </div>
          <div class="tw-space-y-0.5">
            <p class="tw-text-[11px] tw-text-gray-500">Recently active</p>
            <p class="tw-text-base tw-font-semibold tw-text-indigo-600">{{ userActivity.recently_active ?? 0 }}</p>
          </div>
        </div>
      </div>
    </div>

    <!-- ── Timeline ───────────────────────────────────────── -->
    <div class="tw-flex tw-items-center tw-gap-2">
      <iconify-icon icon="solar:timeline-bold-duotone" class="tw-text-sky-500 tw-text-base" />
      <h2 class="tw-text-sm tw-font-semibold tw-text-gray-700">Timeline</h2>
      <div class="tw-flex-1 tw-h-px tw-bg-gray-200 tw-ml-1" />
    </div>
    <div class="tw-grid tw-grid-cols-1 lg:tw-grid-cols-3 tw-gap-2">

      <!-- Meeting dates chart -->
      <div class="tw-bg-white tw-rounded-lg tw-p-4 tw-border tw-border-solid tw-border-gray-200 lg:tw-col-span-2">
        <div class="tw-flex tw-items-center tw-justify-between tw-mb-3">
          <h3 class="tw-text-sm tw-font-semibold tw-text-gray-900">Meeting dates</h3>
          <span class="tw-text-xs tw-text-gray-400">Daily volume · last 30 days</span>
        </div>

        <div v-if="statsLoading" class="tw-h-56 tw-bg-gray-50 tw-rounded tw-animate-pulse" />

        <div v-else-if="!meetingDatesSeries[0]?.data?.length" class="tw-h-56 tw-flex tw-items-center tw-justify-center tw-text-gray-400 tw-text-xs">
          No meeting data yet
        </div>

        <div v-else class="tw-h-56">
          <apexchart
            type="line"
            height="210"
            :options="meetingDatesOptions"
            :series="meetingDatesSeries"
          />
        </div>
      </div>

      <!-- Online Users Widget -->
      <div class="tw-space-y-2">
        <OnlineUsersWidget />
      </div>
    </div>

  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import { useI18n } from 'vue-i18n'
import DashboardStatsCard from '@/components/dashboard/admin/DashboardStatsCard.vue'
import OnlineUsersWidget from '@/components/dashboard/admin/users/OnlineUsersWidget.vue'
import ViewsAnalyticsWidget from '@/components/dashboard/admin/ViewsAnalyticsWidget.vue'
import { admin } from '@/api/dashboard'
import { useRandomWelcome } from '@/composables/useRandomWelcome'

const { locale } = useI18n()
const { selectedWelcome } = useRandomWelcome()

// ── Loading states ─────────────────────────────────────────
const overviewLoading = ref(true)
const statsLoading    = ref(true)

// ── Data ──────────────────────────────────────────────────
const overviewStats  = ref({ companies: 0, users: 0, products: 0, services: 0 })
const meetingStats   = ref({})
const companyPairs   = ref([])
const bestCategories = ref([])
const userActivity   = ref({})
const meetingsTimeline = ref([])

// ── Derived ────────────────────────────────────────────────
const maxCategoryCompanies = computed(() =>
  Math.max(0, ...bestCategories.value.map(c => c.companies))
)

const meetingDatesSeries = computed(() => [{
  name: 'Meetings',
  data: meetingsTimeline.value.map(d => d.total),
}])

const meetingDatesOptions = computed(() => ({
  chart: { id: 'meeting-dates-line', toolbar: { show: false }, zoom: { enabled: false } },
  stroke: { curve: 'smooth', width: 3 },
  dataLabels: { enabled: false },
  xaxis: {
    categories: meetingsTimeline.value.map(d => d.date),
    labels: { style: { fontSize: '10px' }, rotate: -30 },
    axisBorder: { show: false },
    axisTicks: { show: false },
    tickAmount: 7,
  },
  yaxis: { labels: { style: { fontSize: '10px' } }, min: 0, forceNiceScale: true },
  grid: { strokeDashArray: 4, borderColor: '#e5e7eb' },
  colors: ['#2563eb'],
  markers: { size: 3 },
  tooltip: { theme: 'light' },
}))

// ── Helpers ────────────────────────────────────────────────
const getCurrentDate = () => {
  const localeMap = { en: 'en-US', ar: 'ar-SA', fr: 'fr-FR', es: 'es-ES', pt: 'pt-BR' }
  return new Date().toLocaleDateString(localeMap[locale.value] || 'en-US', {
    weekday: 'long', month: 'short', day: 'numeric',
  })
}

const getCurrentTime = () => {
  const localeMap = { en: 'en-US', ar: 'ar-SA', fr: 'fr-FR', es: 'es-ES', pt: 'pt-BR' }
  return new Date().toLocaleTimeString(localeMap[locale.value] || 'en-US', {
    hour: '2-digit', minute: '2-digit',
  })
}

// ── Data fetching ──────────────────────────────────────────
onMounted(async () => {
  // Fire both requests in parallel
  const [overviewRes, statsRes] = await Promise.allSettled([
    admin.overview(),
    admin.stats(),
  ])

  if (overviewRes.status === 'fulfilled') {
    overviewStats.value = overviewRes.value.data
  }
  overviewLoading.value = false

  if (statsRes.status === 'fulfilled') {
    const d = statsRes.value.data
    meetingStats.value   = d.meeting_stats           ?? {}
    companyPairs.value   = d.meetings_by_company_pairs ?? []
    bestCategories.value = d.best_categories         ?? []
    userActivity.value   = d.user_activity           ?? {}
    meetingsTimeline.value = d.meetings_by_date      ?? []
  }
  statsLoading.value = false
})
</script>

<style></style>
